import apiClient from './axios';
import { useAuthStore } from '@/stores/auth-store';
import { transformInvoiceData, transformAdvancePaymentData, transformItemData } from './utils';

// API Types and Interfaces
export interface PaginatedResponse<T> {
  results: T[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  has_next: boolean;
  has_previous: boolean;
  start_index: number;
  end_index: number;
}

export interface Category {
  id: number;
  name: string;
  description: string;
  created_at: string;
}

export interface ShopProfile {
  id: number;
  shop_name: string;
  display_name: string;
  logo: string | null;
  gst_number: string;
  hsn_code: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  phone: string;
  email: string;
  website: string;
  tax_registration_type: 'REGULAR' | 'COMPOSITION' | 'UNREGISTERED';
  cgst_rate: number;
  sgst_rate: number;
  aadhar_required_limit: number;
  returns_policy_label?: string;
  returns_policy_description?: string;
  warranty_label?: string;
  warranty_description?: string;
  care_instructions_label?: string;
  care_instructions_description?: string;
  created_at: string;
  updated_at: string;
}

export interface Supplier {
  id: number;
  name: string;
  contact_person: string;
  phone: string;
  email: string;
  address: string;
  gst_number: string;
  is_active: boolean;
  created_at: string;
}

export interface Item {
  id: number;
  item_code: string;
  huid?: string;
  name: string;
  category: number;
  category_name?: string;
  supplier: number | null;
  supplier_name?: string;
  metal_type: string;
  purity: string;
  gross_weight: number;
  net_weight: number;
  stone_weight: number;
  quantity: number;
  making_charges: number;
  stone_charges: number;
  other_charges: number;
  purchase_price: number;
  selling_price: number;
  description: string;
  size: string;
  design_number: string;
  status: string;
  location: string;
  created_at: string;
  updated_at: string;
  images: ItemImage[];
  primary_image?: string | null;
}

export interface ItemImage {
  id: number;
  item: number;
  image: string;
  caption: string;
  is_primary: boolean;
  created_at: string;
}

export interface StockMovement {
  id: number;
  item: number;
  item_name?: string;
  item_code?: string;
  movement_type: string;
  quantity_change: number;
  reference_number: string;
  notes: string;
  created_by: string;
  created_at: string;
}

export interface PriceHistory {
  id: number;
  item: number;
  item_name?: string;
  item_code?: string;
  old_selling_price: string;
  new_selling_price: string;
  reason: string;
  changed_by: string;
  changed_at: string;
}

export interface Customer {
  id: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  gst_number: string;
  monthly_income?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExchangeItem {
  id: number;
  invoice: number;
  name: string;
  metal_type: string;
  purity: string;
  net_weight: number;
  quantity: number;
  exchange_value: number;
  description: string;
  total_weight: number;
  total_exchange_value: number;
  created_at: string;
  updated_at: string;
}

export interface InvoiceItem {
  id: number;
  item: number;
  item_name: string;
  item_code: string;
  item_thumbnail: string | null;
  quantity: number;
  unit_price: number;
  discount_percentage: number;
  discount_amount: number;
  total_price: number;
  description: string;
  notes: string;
  created_at: string;
  net_weight: number;
  rate_per_gram: number;
  hsn_code: string;
  serial_no: number;
  metal_type: string;
  purity: string;
}

export interface Invoice {
  id: number;
  invoice_number: string;
  customer: number;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  created_by: number;
  created_by_name: string;
  invoice_date: string;
  due_date: string | null;
  status: 'DRAFT' | 'PENDING' | 'PAID' | 'CANCELLED' | 'REFUNDED' | 'EXPIRED';
  subtotal: number;
  tax_rate: number;
  sgst_amount: number;
  cgst_amount: number;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  exchange_value: number;
  exchange_items_description: string;
  exchange_deduction_percentage: number;
  exchange_items: ExchangeItem[];
  additional_charges: Record<string, { label: string; amount: number }>;
  customer_aadhar_number: string;
  aadhar_validation_required: boolean;
  remaining_balance: number;
  exchange_deduction_amount: number;
  additional_charges_total: number;
  notes: string;
  terms_conditions: string;
  items: InvoiceItem[];
  items_count: number;
  created_at: string;
  updated_at: string;
}

export interface Payment {
  id: number;
  invoice: number;
  invoice_number: string;
  customer_name: string;
  amount: string;
  payment_method: 'CASH' | 'CARD' | 'UPI' | 'BANK_TRANSFER' | 'CHEQUE' | 'OTHER';
  payment_status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED' | 'VOID';
  reference_number: string;
  payment_date: string;
  processed_by: number;
  processed_by_name: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface AdvancePayment {
  id: number;
  customer: number;
  customer_name: string;
  customer_email: string;
  invoice: number | null;
  amount: number;
  payment_method: 'CASH' | 'CARD' | 'UPI' | 'BANK_TRANSFER' | 'CHEQUE' | 'OTHER';
  payment_date: string;
  status: 'ACTIVE' | 'USED' | 'REFUNDED' | 'EXPIRED';
  reference_number: string;
  notes: string;
  applied_amount: number;
  applied_date: string | null;
  processed_by: number;
  processed_by_name: string;
  remaining_amount: number;
  is_fully_applied: boolean;
  created_at: string;
  updated_at: string;
}

// Pawn API Types
export interface CustomerPawnProfile {
  id: number;
  customer: number;
  aadhar_number: string;
  photo_url: string;
  kyc_verified: boolean;
  credit_limit: number;
  total_loans_taken: number;
  active_loans_count: number;
  last_loan_date: string | null;
  relation: string;
  customer_rating: 'EXCELLENT' | 'GOOD' | 'AVERAGE' | 'POOR';
  created_at: string;
  updated_at: string;
}

export interface PawnItem {
  id: string;
  loan: string;
  item: number | null;
  item_type: 'GOLD' | 'SILVER' | 'DIAMOND' | 'PLATINUM' | 'OTHER';
  description: string;
  weight: number | null;
  purity: string;
  dimensions: string;
  estimated_value: number;
  appraised_by: number;
  appraisal_date: string;
  photo_urls: string[];
  certificate_urls: string[];
  condition_notes: string;
  return_condition: string;
  is_returned: boolean;
  created_at: string;
}

export interface PawnPayment {
  id: string;
  loan: string;
  amount: number;
  payment_date: string;
  payment_type: 'CASH' | 'UPI' | 'BANK_TRANSFER' | 'CHEQUE' | 'OTHER';
  reference_number: string;
  notes: string;
  created_by: number;
  created_at: string;
}

export interface PawnLoan {
  id: string;
  loan_number: string;
  customer: number;
  principal_amount: number;
  interest_rate: number;
  loan_date: string;
  maturity_date: string;
  actual_return_date: string | null;
  status: 'ACTIVE' | 'OVERDUE' | 'EXTENDED' | 'CLOSED' | 'DEFAULTED';
  grace_period_days: number;
  total_interest_accrued: number;
  total_amount_paid: number;
  outstanding_amount: number;
  penalty_amount: number;
  next_payment_date: string;
  payment_frequency: 'MONTHLY' | 'WEEKLY' | 'DAILY';
  created_by: number;
  notes: string;
  created_at: string;
  updated_at: string;
  pledged_items: PawnItem[];
  payments: PawnPayment[];
}

// Comprehensive API Service using Axios
export class ApiService {
  // Helper method to check if we're in a browser environment
  protected isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  }

  // Dashboard & Statistics
  async getDashboardStats() {
    const response = await apiClient.get('/api/inventory/payments/dashboard_statistics/');
    return response.data;
  }

  async getPawnDashboardStats() {
    const response = await apiClient.get('/api/pawn/loans/dashboard_statistics/');
    return response.data;
  }

  async getTransactionStats(params?: any) {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/api/inventory/payments/transaction_statistics/?${queryParams}` : '/api/inventory/payments/transaction_statistics/';
    const response = await apiClient.get(url);
    return response.data;
  }

  async getProductStats(params?: any) {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/api/inventory/items/product_statistics/?${queryParams}` : '/api/inventory/items/product_statistics/';
    const response = await apiClient.get(url);
    return response.data;
  }

  async getCustomerStats(params?: any) {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/api/inventory/customers/customer_statistics/?${queryParams}` : '/api/inventory/customers/customer_statistics/';
    const response = await apiClient.get(url);
    return response.data;
  }

  // Shop Profile
  async getShopProfile(): Promise<ShopProfile> {
    const response = await apiClient.get('/api/inventory/shop-profile/current/');
    return response.data;
  }

  async updateShopProfile(data: Partial<ShopProfile>): Promise<ShopProfile> {
    const response = await apiClient.put('/api/inventory/shop-profile/current/', data);
    return response.data;
  }

  async updateShopProfileWithFormData(formData: FormData): Promise<ShopProfile> {
    try {
      console.log('Sending FormData to shop profile update endpoint');
      const response = await apiClient.put('/api/inventory/shop-profile/current/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Shop profile update successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Shop profile update failed:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  }

  // Categories
  async getCategories(params?: any): Promise<PaginatedResponse<Category>> {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/api/inventory/categories/?${queryParams}` : '/api/inventory/categories/';
    const response = await apiClient.get(url);
    return response.data;
  }

  async searchCategories(params?: any): Promise<PaginatedResponse<Category> & { suggestions: string[] }> {
    const queryParams = new URLSearchParams(params).toString();
    const url = `/api/inventory/categories/search/?${queryParams}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getCategorySuggestions(searchTerm: string, limit: number = 5): Promise<{ suggestions: string[] }> {
    const params = new URLSearchParams({
      q: searchTerm,
      limit: limit.toString()
    });
    const url = `/api/inventory/categories/suggest/?${params}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async createCategory(data: Partial<Category>): Promise<Category> {
    const response = await apiClient.post('/api/inventory/categories/', data);
    return response.data;
  }

  async updateCategory(id: number, data: Partial<Category>): Promise<Category> {
    const response = await apiClient.put(`/api/inventory/categories/${id}/`, data);
    return response.data;
  }

  async deleteCategory(id: number): Promise<void> {
    await apiClient.delete(`/api/inventory/categories/${id}/`);
  }

  // Items/Products
  async getItems(params?: any): Promise<PaginatedResponse<Item>> {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/api/inventory/items/?${queryParams}` : '/api/inventory/items/';
    const response = await apiClient.get(url);
    return {
      ...response.data,
      results: response.data.results.map(transformItemData)
    };
  }

  async searchItems(searchTerm: string, params?: any): Promise<PaginatedResponse<Item>> {
    const searchParams = new URLSearchParams({
      search: searchTerm,
      ...params
    });
    const url = `/api/inventory/items/?${searchParams.toString()}`;
    const response = await apiClient.get(url);
    return {
      ...response.data,
      results: response.data.results.map(transformItemData)
    };
  }

  async getItem(id: number): Promise<Item> {
    const response = await apiClient.get(`/api/inventory/items/${id}/`);
    return transformItemData(response.data);
  }

  async getItemByCode(itemCode: string): Promise<Item> {
    const response = await apiClient.get(`/api/inventory/items/by_code/?item_code=${itemCode}`);
    return transformItemData(response.data);
  }

  async createItem(formData: FormData): Promise<Item> {
    const response = await apiClient.post('/api/inventory/items/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateItem(id: number, formData: FormData): Promise<Item> {
    const response = await apiClient.put(`/api/inventory/items/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteItem(id: string): Promise<void> {
    await apiClient.delete(`/api/inventory/items/${id}/`);
  }

  // Customers
  async getCustomers(params?: any): Promise<PaginatedResponse<Customer>> {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/api/inventory/customers/?${queryParams}` : '/api/inventory/customers/';
    const response = await apiClient.get(url);
    return response.data;
  }

  async getCustomer(id: number): Promise<Customer> {
    const response = await apiClient.get(`/api/inventory/customers/${id}/`);
    return response.data;
  }

  async createCustomer(data: any): Promise<Customer> {
    const response = await apiClient.post('/api/inventory/customers/', data);
    return response.data;
  }

  async updateCustomer(id: string, data: any): Promise<Customer> {
    const response = await apiClient.put(`/api/inventory/customers/${id}/`, data);
    return response.data;
  }

  async deleteCustomer(id: string): Promise<{ success: boolean }> {
    await apiClient.delete(`/api/inventory/customers/${id}/`);
    return { success: true };
  }

  // Invoices
  async getInvoices(): Promise<PaginatedResponse<Invoice>> {
    const response = await apiClient.get('/api/inventory/invoices/');
    return {
      ...response.data,
      results: response.data.results.map(transformInvoiceData)
    };
  }

  async getInvoice(id: number): Promise<Invoice> {
    const response = await apiClient.get(`/api/inventory/invoices/${id}/`);
    return transformInvoiceData(response.data);
  }

  async getReceipt(paymentId: number): Promise<any> {
    try {
      // First get payment details
      const paymentResponse = await apiClient.get(`/api/inventory/payments/${paymentId}/`);
      const payment = paymentResponse.data;
      
      if (!payment.invoice) {
        throw new Error('Payment not associated with any invoice');
      }
      
      // Then get invoice details
      const invoiceResponse = await apiClient.get(`/api/inventory/invoices/${payment.invoice}/`);
      const invoice = invoiceResponse.data;
      
      // Get customer details
      const customerResponse = await apiClient.get(`/api/inventory/customers/${invoice.customer}/`);
      const customer = customerResponse.data;
      
      // Get shop profile
      const shopProfileResponse = await apiClient.get('/api/inventory/shop-profile/current/');
      const shopProfile = shopProfileResponse.data;
      
      // Format receipt data
      const receiptData = {
        receiptNumber: invoice.invoice_number,
        additional_charges_total: invoice.additional_charges_total,
        date: invoice.created_at,
        customer: {
          name: invoice.customer_name,
          email: invoice.customer_email,
          phone: invoice.customer_phone,
          address: invoice.customer_address || 'N/A'
        },
        items: invoice.items.map((item: any) => ({
          sku: item.item_code,
          name: item.item_name,
          huid: item.huid || 'N/A',
          quantity: item.quantity,
          unitPrice: parseFloat(item.unit_price),
          total: parseFloat(item.total_price),
          netWeight: parseFloat(item.net_weight || 0),
          purity: item.purity || 'N/A',
          metalType: item.metal_type || 'N/A',
          ratePerGram: parseFloat(item.rate_per_gram || 0),
          hsnCode: item.hsn_code || '7113',
          serialNo: item.serial_no,
          description: item.description || item.item_name
        })),
        subtotal: parseFloat(invoice.subtotal),
        sgst: parseFloat(invoice.sgst_amount),
        cgst: parseFloat(invoice.cgst_amount),
        tax: parseFloat(invoice.tax_amount),
        total: parseFloat(invoice.total_amount),
        status: invoice.status,
        payment: {
          method: payment.payment_method,
          transactionId: payment.reference_number
        },
        cashier: payment.processed_by_name,
        shop: {
          name: shopProfile.shop_name,
          displayName: shopProfile.display_name || shopProfile.shop_name,
          logo: shopProfile.logo,
          address: shopProfile.full_address || `${shopProfile.address}, ${shopProfile.city}, ${shopProfile.state} - ${shopProfile.postal_code}`,
          phone: shopProfile.phone,
          email: shopProfile.email,
          gst: shopProfile.gst_number,
          hsn_code: shopProfile.hsn_code,
          sgst_rate: parseFloat(shopProfile.sgst_rate),
          cgst_rate: parseFloat(shopProfile.cgst_rate),
          returns_policy_label: shopProfile.returns_policy_label,
          returns_policy_description: shopProfile.returns_policy_description,
          warranty_label: shopProfile.warranty_label,
          warranty_description: shopProfile.warranty_description,
          care_instructions_label: shopProfile.care_instructions_label,
          care_instructions_description: shopProfile.care_instructions_description
        },
        notes: invoice.notes,
        discount_amount: parseFloat(invoice.discount_amount),
        exchange_value: parseFloat(invoice.exchange_value),
        exchange_items_description: invoice.exchange_items_description,
        exchange_deduction_percentage: parseFloat(invoice.exchange_deduction_percentage),
        exchange_deduction_amount: parseFloat(invoice.exchange_deduction_amount),
        exchange_items: invoice.exchange_items.map((item: any) => ({
          name: item.name,
          metalType: item.metal_type,
          purity: item.purity,
          netWeight: parseFloat(item.net_weight),
          quantity: item.quantity,
          exchangeValue: parseFloat(item.exchange_value),
          description: item.description
        })),
        payment_history: invoice.payment_history,
        payment_summary: invoice.payment_summary,
        amountInWords: this.numberToWords(parseFloat(invoice.total_amount))
      };
      
      return receiptData;
    } catch (error) {
      console.error('Error generating receipt:', error);
      throw error;
    }
  }

  private numberToWords(number: number): string {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    const convertLessThanOneThousand = (n: number): string => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' and ' + convertLessThanOneThousand(n % 100) : '');
    };
    
    if (number === 0) return 'Zero';
    if (number < 1000) return convertLessThanOneThousand(number);
    if (number < 100000) return convertLessThanOneThousand(Math.floor(number / 1000)) + ' Thousand' + (number % 1000 !== 0 ? ' ' + convertLessThanOneThousand(number % 1000) : '');
    return convertLessThanOneThousand(Math.floor(number / 100000)) + ' Lakh' + (number % 100000 !== 0 ? ' ' + convertLessThanOneThousand(number % 100000) : '');
  }

  async getReceiptByInvoice(invoiceId: number): Promise<any> {
    const response = await apiClient.get(`/api/inventory/invoices/${invoiceId}/`);
    return response.data;
  }

  async createInvoice(data: any): Promise<Invoice> {
    const response = await apiClient.post('/api/inventory/invoices/', data);
    return response.data;
  }

  async addInvoiceItem(invoiceId: number, data: any): Promise<any> {
    const response = await apiClient.post(`/api/inventory/invoices/${invoiceId}/add_item/`, data);
    return response.data;
  }

  async updateInvoice(id: number, data: any): Promise<Invoice> {
    const response = await apiClient.put(`/api/inventory/invoices/${id}/`, data);
    return response.data;
  }

  async deleteInvoice(id: number): Promise<void> {
    await apiClient.delete(`/api/inventory/invoices/${id}/`);
  }

  // Payments
  async getPayments(params?: any): Promise<PaginatedResponse<Payment>> {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/api/inventory/payments/?${queryParams}` : '/api/inventory/payments/';
    const response = await apiClient.get(url);
    return response.data;
  }

  async getPayment(id: number): Promise<Payment> {
    const response = await apiClient.get(`/api/inventory/payments/${id}/`);
    return response.data;
  }

  async voidPayment(id: number): Promise<{ message: string; payment_id: number; invoice_number: string | null }> {
    const response = await apiClient.post(`/api/inventory/payments/${id}/void_payment/`);
    return response.data;
  }

  async getInvoicePayments(invoiceId: number): Promise<Payment[]> {
    const response = await apiClient.get(`/api/inventory/invoices/${invoiceId}/payments/`);
    return response.data;
  }

  async addInvoicePayment(invoiceId: number, data: any): Promise<Payment> {
    const response = await apiClient.post(`/api/inventory/invoices/${invoiceId}/add_payment/`, data);
    return response.data;
  }

  async processPayment(invoiceId: number, data: any): Promise<{ payment: Payment; invoice_status: string; total_paid: number; remaining_balance: number }> {
    const response = await apiClient.post(`/api/inventory/invoices/${invoiceId}/process_payment/`, data);
    return response.data;
  }

  // Pawn Loans
  async getPawnLoans(): Promise<PaginatedResponse<PawnLoan>> {
    const response = await apiClient.get('/api/pawn/loans/');
    return response.data;
  }

  async getPawnLoan(id: string): Promise<PawnLoan> {
    const response = await apiClient.get(`/api/pawn/loans/${id}/`);
    return response.data;
  }

  async createPawnLoan(data: any): Promise<PawnLoan> {
    const response = await apiClient.post('/api/pawn/loans/', data);
    return response.data;
  }

  async makePawnPayment(loanId: string, data: any): Promise<PawnPayment> {
    const response = await apiClient.post(`/api/pawn/loans/${loanId}/payments/`, data);
    return response.data;
  }

  // Advance Payments
  async getAdvancePayments(params?: any): Promise<PaginatedResponse<AdvancePayment>> {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/api/inventory/advance-payments/?${queryParams}` : '/api/inventory/advance-payments/';
    const response = await apiClient.get(url);
    return {
      ...response.data,
      results: response.data.results.map(transformAdvancePaymentData)
    };
  }

  async getAdvancePayment(id: number): Promise<AdvancePayment> {
    const response = await apiClient.get(`/api/inventory/advance-payments/${id}/`);
    return transformAdvancePaymentData(response.data);
  }

  async createAdvancePayment(data: any): Promise<AdvancePayment> {
    const response = await apiClient.post('/api/inventory/advance-payments/', data);
    return response.data;
  }

  async updateAdvancePayment(id: number, data: any): Promise<AdvancePayment> {
    const response = await apiClient.put(`/api/inventory/advance-payments/${id}/`, data);
    return response.data;
  }

  async deleteAdvancePayment(id: number): Promise<void> {
    await apiClient.delete(`/api/inventory/advance-payments/${id}/`);
  }

  async getAdvancePaymentStats(params?: any): Promise<any> {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/api/inventory/advance-payments/statistics/?${queryParams}` : '/api/inventory/advance-payments/statistics/';
    const response = await apiClient.get(url);
    return response.data;
  }

  async applyAdvancePayment(advanceId: number, invoiceId: number, amount: number): Promise<any> {
    const response = await apiClient.post(`/api/inventory/advance-payments/${advanceId}/apply-to-invoice/`, {
      invoice_id: invoiceId,
      amount_to_apply: amount
    });
    return response.data;
  }

  async applyMultipleAdvances(invoiceId: number, advancesData: any[]): Promise<any> {
    const response = await apiClient.post(`/api/inventory/invoices/${invoiceId}/apply_multiple_advances/`, {
      advances: advancesData
    });
    return response.data;
  }

  async removeAppliedAdvance(advanceId: number, invoiceId: number): Promise<any> {
    const response = await apiClient.post(`/api/inventory/advance-payments/${advanceId}/remove-from-invoice/`, {
      invoice_id: invoiceId
    });
    return response.data;
  }

  // Additional methods
  async getPendingPaymentInvoices(): Promise<PaginatedResponse<Invoice>> {
    const response = await apiClient.get('/api/inventory/invoices/with_balance_due/');
    return response.data;
  }

  async getActiveCustomers(params?: any): Promise<PaginatedResponse<Customer>> {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/api/inventory/customers/active/?${queryParams}` : '/api/inventory/customers/active/';
    const response = await apiClient.get(url);
    return response.data;
  }

  async searchCustomers(searchTerm: string, params?: any): Promise<PaginatedResponse<Customer>> {
    const searchParams = new URLSearchParams({
      search: searchTerm,
      ...params
    });
    const url = `/api/inventory/customers/?${searchParams.toString()}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getTransactionStatistics(): Promise<any> {
    const response = await apiClient.get('/api/inventory/payments/transaction_statistics/');
    return response.data;
  }

  async getStockStatistics(): Promise<any> {
    const response = await apiClient.get('/api/inventory/items/stock_statistics/');
    return response.data;
  }

  async getStockProducts(params?: any): Promise<PaginatedResponse<Item>> {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/api/inventory/items/stock_products/?${queryParams}` : '/api/inventory/items/stock_products/';
    const response = await apiClient.get(url);
    return response.data;
  }

  async getCustomerAdvanceBalance(customerId: number): Promise<any> {
    const response = await apiClient.get(`/api/inventory/customers/${customerId}/advance_balance/`);
    return response.data;
  }

  async applyAdvanceToInvoice(advanceId: number, data: any): Promise<any> {
    const response = await apiClient.post(`/api/inventory/advance-payments/${advanceId}/apply-to-invoice/`, data);
    return response.data;
  }

  async updateInvoiceStatus(invoiceId: number, status: string): Promise<any> {
    const response = await apiClient.post(`/api/inventory/invoices/${invoiceId}/update_status/`, { status });
    return response.data;
  }

  async createInvoicePaymentSchedule(invoiceId: number, data: any): Promise<any> {
    const response = await apiClient.post(`/api/inventory/invoices/${invoiceId}/create_payment_schedule/`, data);
    return response.data;
  }

  async getInvoicePaymentHistory(invoiceId: number): Promise<any> {
    const response = await apiClient.get(`/api/inventory/invoices/${invoiceId}/payment_history/`);
    return response.data;
  }

  async createStaff(data: any): Promise<any> {
    const response = await apiClient.post('/api/inventory/staff/', data);
    return response.data;
  }

  async createSupplier(data: any): Promise<any> {
    const response = await apiClient.post('/api/inventory/suppliers/', data);
    return response.data;
  }

  async getStaff(): Promise<PaginatedResponse<any>> {
    const response = await apiClient.get('/api/inventory/staff/');
    return response.data;
  }

  async getSuppliers(): Promise<PaginatedResponse<Supplier>> {
    const response = await apiClient.get('/api/inventory/suppliers/');
    return response.data;
  }

  async testPayments(): Promise<any> {
    const response = await apiClient.get('/api/inventory/payments/test_payments/');
    return response.data;
  }

  // Export Functions
  async exportProducts(params?: any): Promise<Blob> {
    const response = await apiClient.get('/api/inventory/items/export_stock_products/', {
      responseType: 'blob'
    });
    return response.data;
  }

  async exportPayments(params?: any): Promise<Blob> {
    const response = await apiClient.get('/api/inventory/payments/export_payments/', {
      responseType: 'blob'
    });
    return response.data;
  }

  async exportCustomers(): Promise<Blob> {
    const response = await apiClient.get('/api/inventory/customers/export_customers/', {
      responseType: 'blob'
    });
    return response.data;
  }
}

// Create singleton instance
export const apiService = new ApiService(); 